import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { cleanupBlobUrls } from '../utils/fileUtils';

const AppStateContext = createContext();

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};

const initialState = {
  currentTool: 'pdf-to-image',
  selectedFile: null,

  // PDF 相關
  pdfDocument: null,
  currentPage: 1,
  totalPages: 0,
  pageRange: 'all',
  customPageRange: '',

  // 轉換設置
  imageFormat: 'image/jpeg',
  imageQuality: 80,
  compressionLevel: 'medium',

  // 圖片相關
  imagePreview: null,
  originalWidth: 0,
  originalHeight: 0,
  targetWidth: 800,
  targetHeight: 600,
  maintainAspectRatio: true,

  // 結果相關 - 保存 blob 和 URL
  convertedPages: [],
  convertedImageBlob: null,
  convertedImageUrl: null,
  resizedImageBlob: null,
  resizedImageUrl: null,
  compressedImageBlob: null,
  compressedImageUrl: null,
  compressedPdfBlob: null,
  compressedPdfUrl: null,

  // 統計信息
  originalSize: 0,
  processedSize: 0,

  // UI 狀態
  isLoading: false,
  showResults: false,
  showSettings: false,
  error: null
};

function appStateReducer(state, action) {
  switch (action.type) {
    case 'SET_CURRENT_TOOL':
      cleanupStateUrls(state);
      return { ...initialState, currentTool: action.payload };

    case 'SET_SELECTED_FILE':
      return {
        ...state,
        selectedFile: action.payload,
        showSettings: true,
        showResults: false,
        error: null,
        originalSize: action.payload.size
      };

    case 'SET_PDF_DOCUMENT':
      return {
        ...state,
        pdfDocument: action.payload.pdf,
        totalPages: action.payload.numPages
      };

    case 'SET_IMAGE_PREVIEW':
      return {
        ...state,
        imagePreview: action.payload.url,
        originalWidth: action.payload.width,
        originalHeight: action.payload.height,
        targetWidth: action.payload.width,
        targetHeight: action.payload.height
      };

    case 'UPDATE_SETTINGS':
      return { ...state, ...action.payload };

    case 'SET_CONVERTED_PAGES':
      return {
        ...state,
        convertedPages: action.payload,
        showResults: true,
        isLoading: false
      };

    case 'SET_CONVERTED_IMAGE':
      // 🆕 改進：清理舊的 URL
      if (state.convertedImageUrl) {
        cleanupBlobUrls(state.convertedImageUrl);
      }
      return {
        ...state,
        convertedImageBlob: action.payload.blob,
        convertedImageUrl: action.payload.url,
        processedSize: action.payload.size,
        showResults: true,
        isLoading: false
      };

    case 'SET_RESIZED_IMAGE':
      if (state.resizedImageUrl) {
        cleanupBlobUrls(state.resizedImageUrl);
      }
      return {
        ...state,
        resizedImageBlob: action.payload.blob,
        resizedImageUrl: action.payload.url,
        processedSize: action.payload.size,
        showResults: true,
        isLoading: false
      };

    case 'SET_COMPRESSED_IMAGE':
      if (state.compressedImageUrl) {
        cleanupBlobUrls(state.compressedImageUrl);
      }
      return {
        ...state,
        compressedImageBlob: action.payload.blob,
        compressedImageUrl: action.payload.url,
        processedSize: action.payload.size,
        showResults: true,
        isLoading: false
      };

    case 'SET_COMPRESSED_PDF':
      if (state.compressedPdfUrl) {
        cleanupBlobUrls(state.compressedPdfUrl);
      }
      return {
        ...state,
        compressedPdfBlob: action.payload.blob,
        compressedPdfUrl: action.payload.url,
        processedSize: action.payload.size,
        showResults: true,
        isLoading: false
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload, error: null };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'RESET_TOOL':
      cleanupStateUrls(state);
      return { ...initialState, currentTool: state.currentTool };

    default:
      return state;
  }
}

// 🆕 改進：使用統一的清理函數
function cleanupStateUrls(state) {
  const urlsToClean = [
    state.imagePreview,
    state.convertedImageUrl,
    state.resizedImageUrl,
    state.compressedImageUrl,
    state.compressedPdfUrl,
    ...state.convertedPages.map(page => page.url)
  ].filter(Boolean);

  cleanupBlobUrls(urlsToClean);
  
  console.log(`已清理 ${urlsToClean.length} 個 Blob URLs`);
}

export const AppStateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState);

  // 🆕 改進：組件卸載時清理所有資源
  useEffect(() => {
    return () => {
      console.log('AppStateProvider 卸載，清理所有資源...');
      cleanupStateUrls(state);
    };
  }, []);

  // 🆕 定期檢查記憶體使用（開發模式）
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const interval = setInterval(() => {
        if (performance.memory) {
          const used = Math.round(performance.memory.usedJSHeapSize / 1024 / 1024);
          const total = Math.round(performance.memory.totalJSHeapSize / 1024 / 1024);
          console.log(`記憶體使用: ${used}MB / ${total}MB`);
        }
      }, 30000); // 每 30 秒檢查一次

      return () => clearInterval(interval);
    }
  }, []);

  const value = { state, dispatch };

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};